import { useEffect, useState } from 'react';
import { CatalogApi } from '@backstage/catalog-client';
import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { RuleEntity } from '@internal/backstage-plugin-catalog-devops-common';

export const useGroups = () => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [groups, setGroups] = useState<Entity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all groups
        const groupFilter = { kind: 'group' };
        const groupResponse = await catalogApi.getEntities({
          filter: groupFilter,
        });
        setGroups(groupResponse.items);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi]);

  return { groups };
};

export const useUsers = () => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [users, setUsers] = useState<Entity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all users
        const userFilter = { kind: 'user' };
        const userResponse = await catalogApi.getEntities({
          filter: userFilter,
        });
        setUsers(userResponse.items);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi]);

  return { users };
};

export const useDomains = () => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [domains, setDomains] = useState<Entity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all domains
        const domainFilter = { kind: 'domain' };
        const domainResponse = await catalogApi.getEntities({
          filter: domainFilter,
        });
        setDomains(domainResponse.items);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi]);

  return { domains };
};

export const useTemplates = () => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [templates, setTemplates] = useState<Entity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all templates
        const templateFilter = { kind: 'template' };
        const templateResponse = await catalogApi.getEntities({
          filter: templateFilter,
        });
        setTemplates(templateResponse.items);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi]);

  return { templates };
};

export const useAccounts = () => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [accounts, setAccounts] = useState<Entity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all accounts
        const accountFilter = { kind: 'account' };
        const accountResponse = await catalogApi.getEntities({
          filter: accountFilter,
        });
        setAccounts(accountResponse.items);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi]);

  return { accounts };
};

export const useCloudResources = () => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [cloudResources, setCloudResources] = useState<Entity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all cloud resources
        const cloudResourceFilter = { kind: 'cloudresource' };
        const cloudResourceResponse = await catalogApi.getEntities({
          filter: cloudResourceFilter,
        });
        setCloudResources(cloudResourceResponse.items);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi]);

  return { cloudResources };
};

export const useAccountCloudResources = (entityName: string) => {
  const { cloudResources } = useCloudResources();
  const [accountCloudResources, setAccountCloudResources] = useState<Entity[]>(
    [],
  );

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const filteredResources = cloudResources.filter(
          resource => resource.spec!.account === entityName,
        );
        setAccountCloudResources(filteredResources);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [cloudResources, entityName]);

  return { accountCloudResources };
};

export const useCloudResourcesAffectedByRuleName = (entityName: string) => {
  const { cloudResources } = useCloudResources();
  const [affectedCloudResources, setAffectedCloudResources] = useState<
    Entity[]
  >([]);

  const filterResources = (resources: Entity[]) =>
    resources.filter(resource =>
      resource.relations!.find(
        relation =>
          relation.type === 'affectedBy' &&
          relation.targetRef === `rule:default/${entityName}`,
      ),
    );

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const filteredResources = filterResources(cloudResources);
        setAffectedCloudResources(filteredResources);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [cloudResources, entityName]);

  return { affectedCloudResources };
};

export const useRulesAffectsCloudResource = (entityName: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [rules, setRules] = useState<Entity[]>([]);

  const filterRules = (rulesToFilter: Entity[]) =>
    rulesToFilter.filter(rule =>
      rule.relations!.find(
        relation =>
          relation.type === 'affects' &&
          relation.targetRef === `cloudresource:default/${entityName}`,
      ),
    );

  useEffect(() => {
    let cancelled = false;
    const fetchEntities = async () => {
      const filter = {
        kind: 'rule',
      };
      const loadedRules = await catalogApi.getEntities({ filter });
      const filteredRules = filterRules(loadedRules.items);

      if (!cancelled) {
        setRules(filteredRules);
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, entityName]);

  return { rules };
};

export const useCloudResourcesAffectedByRule = (
  selectedRule: RuleEntity | undefined,
) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [affectedCloudResources, setAffectedCloudResources] = useState<
    Entity[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    const fetchAffectedResources = async () => {
      const resourceRefs =
        selectedRule?.relations
          ?.filter(relation => relation.type === 'affects')
          .map(relation => relation.targetRef) ?? [];
      const loadedResources = await catalogApi.getEntitiesByRefs({
        entityRefs: resourceRefs,
      });
      if (!cancelled) {
        setAffectedCloudResources(
          loadedResources.items.filter(resource => resource !== undefined),
        );
      }
    };
    fetchAffectedResources();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, selectedRule]);

  return { affectedCloudResources };
};

export const useRulesByEntity = (entity: Entity) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [rules, setRules] = useState<Entity[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchEntities = async () => {
      const relatedRefs =
        entity.relations
          ?.filter(relation => relation.type === 'affectedBy')
          .map(relation => relation.targetRef) ?? [];
      const loadedRules = await catalogApi.getEntitiesByRefs({
        entityRefs: relatedRefs,
      });
      if (!cancelled) {
        setRules(loadedRules.items.filter(rule => rule !== undefined));
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, entity]);

  return { rules };
};

export const useRules = (accountName: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [rules, setRules] = useState<Entity[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchEntities = async () => {
      const filter = {
        kind: 'rule',
        'spec.account': accountName,
      };
      const filteredRules = await catalogApi.getEntities({ filter });

      if (!cancelled) {
        setRules(filteredRules.items);
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [accountName, catalogApi]);

  return { rules };
};

export const useFinOpsRules = (entityName: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [finOpsRules, setFinOpsRules] = useState<Entity[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchEntities = async () => {
      const filter = {
        'spec.account': entityName,
        'spec.category': 'Finance',
      };
      const filteredRules = await catalogApi.getEntities({ filter });

      if (!cancelled) {
        setFinOpsRules(filteredRules.items);
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [entityName, catalogApi]);

  return { finOpsRules };
};

export const useSecurityRules = (entityName: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [securityRules, setSecurityRules] = useState<Entity[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchEntities = async () => {
      const filter = {
        'spec.account': entityName,
        'spec.category': 'Security',
      };
      const filteredRules = await catalogApi.getEntities({ filter });

      if (!cancelled) {
        setSecurityRules(filteredRules.items);
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [entityName, catalogApi]);

  return { securityRules };
};

export const useHousekeepingRules = (entityName: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [housekeepingRules, setHousekeepingRules] = useState<Entity[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchEntities = async () => {
      const filter = {
        'spec.account': entityName,
        'spec.category': 'Housekeeping',
      };
      const filteredRules = await catalogApi.getEntities({ filter });

      if (!cancelled) {
        setHousekeepingRules(filteredRules.items);
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [entityName, catalogApi]);

  return { housekeepingRules };
};

export const useClusters = (entityName: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [clusters, setClusters] = useState<Entity[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchEntities = async () => {
      const filter = {
        kind: 'Cluster',
        'spec.account': entityName,
      };
      const filteredRules = await catalogApi.getEntities({ filter });

      if (!cancelled) {
        setClusters(filteredRules.items);
      }
    };

    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [entityName, catalogApi]);

  return { clusters };
};

export const useRulesByRuleSet = (ruleSetName: string) => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [rules, setRules] = useState<Entity[]>([]);

  useEffect(() => {
    if (!ruleSetName) {
      setRules([]);
      return;
    }
    const fetchEntities = async () => {
      // The rule→ruleset link is the partOf relation; spec.ruleSets is the
      // denormalized copy, so filter on the relation.
      const filter = {
        kind: 'rule',
        'relations.partOf': `ruleset:default/${ruleSetName}`,
      };
      const filteredRules = await catalogApi.getEntities({ filter });

      setRules(filteredRules.items);
    };

    fetchEntities();
  }, [catalogApi, ruleSetName]);

  return { rules };
};

export const useIntegrations = () => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [integrations, setIntegrations] = useState<Entity[]>([]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all integrations
        const integrationFilter = { kind: 'integration' };
        const integrationResponse = await catalogApi.getEntities({
          filter: integrationFilter,
        });
        setIntegrations(integrationResponse.items);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi]);

  return { integrations };
};

export const useEntityOfKindAndName = (
  entityKind: string,
  entityName: string,
): Entity | undefined => {
  const catalogApi = useApi<CatalogApi>(catalogApiRef);
  const [entity, setEntity] = useState<Entity | undefined>(undefined);

  const kind = entityKind.toLowerCase() === 'project' ? 'Domain' : entityKind;

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        // Fetch all integrations
        const filter = {
          kind: kind,
          'metadata.name': entityName,
        };
        const response = await catalogApi.getEntities({ filter: filter });
        setEntity(response.items[0]);
      } catch (e) {
        // Ignore error
      }
    };

    fetchEntities();
  }, [catalogApi, entityKind, entityName]);

  return entity;
};
